<template>
  <login-layout>
    <template #body>
      <atom-info-box>
        <p>
          Additional verification is required to complete your sign in.
        </p>
        <p>
          We have emailed you with a 6-digit code. Please enter it here.
        </p>
      </atom-info-box>

      <atom-text-input id="mfa-code-input" v-model="mfaToken" label="Sign in code"
                       :error-message="validationError.message"/>

      <atom-boolean-checkbox
        id="remember-device-checkbox"
        v-model="rememberDevice"
        :label="rememberDeviceMessage"
        hint-text="You will still need to provide your username and password. This is not advised for shared computers."/>

      <atom-button label="Sign in" type="primary" button-type="submit" @click.prevent="handleSubmitToken"/>

      <div class="details-wrapper">
        <atom-details summary-text="Haven't received a sign in code?">
          <p>
            It might take a few minutes for the email to arrive. Please check your spam folder if it's not in your inbox.
          </p>
          <p>
            You can <a :href="loginUrl">sign in again to resend the code</a>.
          </p>
        </atom-details>
      </div>
    </template>
  </login-layout>
</template>

<script>
import { useVuelidate } from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";
import LoginLayout from "authentication/components/login/layout/LoginLayout.vue";
import { saveBrowser } from "authentication/js/api/saved-browsers.api";
import { getMfaSettings } from "authentication/js/api/user-authentication-settings.api";
import { useLoginPropertiesPublicStore } from "authentication/stores/login-properties.store";
import { useLoginStore } from "authentication/stores/login.store";
import AtomBooleanCheckbox from "core/components/atom/booleanCheckbox/AtomBooleanCheckbox.vue";
import AtomButton from "core/components/atom/button/AtomButton.vue";
import AtomDetails from "core/components/atom/details/AtomDetails.vue";
import AtomInfoBox from "core/components/atom/infoBox/AtomInfoBox.vue";
import AtomTextInput from "core/components/atom/textInput/AtomTextInput.vue";
import springUrl from "core/js/spring-url.js";
import { pluralise } from "core/js/string-utils.js";
import { getFirstValidationError } from "core/js/validation/validation-messages.js";
import { useCurrentSessionStore } from "core/store/current-session.store";
import { useUserStore } from "core/store/user.store";
import { template } from "lodash";
import { storeToRefs } from "pinia";

const LOGIN_PATH = "/authentication/login";

export default {
  components: {
    AtomDetails,
    AtomTextInput,
    AtomBooleanCheckbox,
    AtomButton,
    LoginLayout,
    AtomInfoBox,
  },
  setup() {
    const loginPropertiesStore = useLoginPropertiesPublicStore();
    const loginStore = useLoginStore();
    const { handleLoginWithToken } = loginStore;
    const { getLatestLoginResult, loginSuccess, loginFailure, tokenNotFound, retryLimitExceeded } = storeToRefs(loginStore);

    return {
      loginPropertiesStore,
      getLatestLoginResult,
      loginSuccess,
      loginFailure,
      tokenNotFound,
      retryLimitExceeded,
      handleLoginWithToken,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      tokenParamMissing: false,
      validationError: {},
      userStore: undefined,
      currentSessionStore: undefined,
      mfaSettings: undefined,
      mfaToken: "",
      rememberDevice: false,
    };
  },
  validations() {
    return {
      mfaToken: {
        required,
        minLengthValue: minLength(6),
        maxLengthValue: maxLength(6),
      },
    };
  },
  computed: {
    rememberDeviceMessage() {
      return `Remember this device for ${this.mfaDisplayDuration} ${pluralise("day", this.mfaDisplayDuration)}`;
    },
    loginUrl() {
      return springUrl(LOGIN_PATH);
    },
    mfaDisplayDuration() {
      return this.mfaSettings?.durationDays;
    },
  },
  async mounted() {
    this.mfaSettings = await getMfaSettings();
  },
  methods: {
    async handleSubmitToken() {
      await this.submitToken(this.mfaToken);

      if (this.loginSuccess) {
        this.userStore = useUserStore();
        this.currentSessionStore = useCurrentSessionStore();

        await Promise.all([this.userStore.isReady(), this.currentSessionStore.isReady()]);

        if (this.rememberDevice) {
          await saveBrowser();
        }
        this.redirectToLoginResultUrl();
      } else if (this.tokenNotFound || this.retryLimitExceeded) {
        this.redirectToSignIn();
      } else {
        this.validationError = {
          reason: this.getLatestLoginResult.loginFailure.failureReason,
          message: this.getLatestLoginResult.loginFailure.message,
        };
      }
    },
    async submitToken(token) {
      this.validationError = {};

      if (await this.v$.$validate()) {
        await this.handleLoginWithToken(token);
      } else {
        this.validationError = {
          message: getFirstValidationError(
            this.v$.mfaToken,
            "Sign in code",
            {
              required: template("Enter the sign in code"),
              minLengthValue: template("Enter a six digit code"),
              maxLengthValue: template("Enter a six digit code"),
            },
          ),
        };
      }
    },
    redirectToLoginResultUrl() {
      window.location.assign(this.getLatestLoginResult.redirectUrl);
    },
    redirectToSignIn() {
      this.$router.push({
        name: "login-page",
        query: {
          credentialLogin: "true",
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "core/scss/helpers/colour" as colour;
@use "core/scss/helpers/hint" as hint;

.details-wrapper {
  display: flex;
  padding-top: 1rem;
}
</style>
